import { useEffect, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { matchPath, NavLink, useLocation } from 'react-router-dom';

import { BUY_IT_AGAIN_AISLE, MEMBER_DEALS_AISLE, NEW_AISLES } from 'constants/shop';
import { IMPERFECT_THEME_DOMAIN, MISFITS_THEME_DOMAIN } from 'constants/theme';
import { AisleIcon } from 'honeydew/icons/Aisles';
import Tag from 'honeydew/Tag';
import useGlobalStore from 'hooks/useGlobalStore';
import { buildMobileL1AisleList, getAisleIconName, isAisleActive } from 'utils/aislesUtils';
import { trackAisleNavigation } from 'utils/analyticsUtils';
import { getAislePath } from 'utils/pathUtils';

const isHardCodedAisleActive = (aisleURL, location) => {
    switch (aisleURL) {
        case '/shop': {
            const matchHomePath =
                matchPath('/shop', location.pathname) ||
                matchPath('/shop/browse/misfits+perks', location.pathname) ||
                matchPath('/shop/aisle/misfits+perks', location.pathname) ||
                matchPath(BUY_IT_AGAIN_AISLE.url, location.pathname) ||
                matchPath(MEMBER_DEALS_AISLE.url, location.pathname);
            return !!matchHomePath;
        }

        default:
            return !!matchPath(aisleURL, location.pathname);
    }
};

const ICON_SIZE_DEFAULT = 40;
const ICON_SIZE_ACTIVE = 60;

function scrollActiveIntoCenter(nav, activeEl) {
    if (!nav || !activeEl) return;
    const navWidth = nav.clientWidth;
    const elLeft = activeEl.offsetLeft;
    const elWidth = activeEl.offsetWidth;
    const scrollTarget = elLeft - navWidth / 2 + elWidth / 2;
    nav.scrollTo({ left: Math.max(0, scrollTarget), behavior: 'smooth' });
}

export function L1Navigation({ aisles = [] }) {
    const location = useLocation();
    const { isMisfitsTheme } = useGlobalStore();
    const navRef = useRef(null);
    const activeRef = useRef(null);
    const wasInViewRef = useRef(true);

    const { inView, ref: inViewRef } = useInView({ threshold: 0 });

    const L1Aisles = buildMobileL1AisleList(aisles);

    useEffect(() => {
        scrollActiveIntoCenter(navRef.current, activeRef.current);
    }, [location.pathname, aisles.length]);

    useEffect(() => {
        if (!inView && wasInViewRef.current) {
            scrollActiveIntoCenter(navRef.current, activeRef.current);
        }
        wasInViewRef.current = inView;
    }, [inView]);

    const aisleItems = L1Aisles.map((aisle) => {
        const aisleIconName = getAisleIconName(aisle.name, isMisfitsTheme);
        const isHardCodedAisle = !!aisle.url;
        const to = isHardCodedAisle ? aisle.url : getAislePath(aisle.name);
        const isActive = isHardCodedAisle
            ? isHardCodedAisleActive(aisle.url, location)
            : isAisleActive(aisle.name, location);
        return (
            <li
                key={aisle.id}
                ref={isActive ? activeRef : undefined}
                className={clsx('flex', isActive && 'self-start')}
            >
                <NavLink
                    to={to}
                    className={clsx(
                        'flex flex-col items-center px-15 leading-[16px]! text-nowrap',
                        isActive ? 'justify-start' : 'justify-center'
                    )}
                    onClick={() => {
                        trackAisleNavigation({
                            aisleName: aisle.name,
                            level: 1,
                            mobile: true,
                            uiElement: 'mobile L1',
                        });
                    }}
                >
                    <span
                        className={clsx('flex flex-col items-center justify-start', isActive ? 'h-[60px]' : 'h-[40px]')}
                    >
                        <AisleIcon
                            aisleIconName={aisleIconName}
                            isActive={isActive}
                            width={isActive ? ICON_SIZE_ACTIVE : ICON_SIZE_DEFAULT}
                            brand={isMisfitsTheme ? MISFITS_THEME_DOMAIN : IMPERFECT_THEME_DOMAIN}
                        />
                        {NEW_AISLES.includes(aisle.name) && (
                            <span className={clsx('-ml-3 self-start', isActive ? '-mt-[60px]' : '-mt-11')}>
                                <Tag fontSize="mobileXXS" bgColor="primary" variant="dark" rounded>
                                    NEW
                                </Tag>
                            </span>
                        )}
                    </span>
                    <span
                        className={clsx('text-center', isActive ? 'text-body-xs font-grotesk-bold' : 'text-body-xxs')}
                    >
                        {aisle.name}
                    </span>
                </NavLink>
            </li>
        );
    });

    return (
        <nav>
            <ul
                ref={(el) => {
                    navRef.current = el;
                    inViewRef(el);
                }}
                className="scrollbar-hide flex h-[85px] shrink-0 touch-pan-x items-center overflow-x-auto overflow-y-hidden border-b border-b-off-white bg-white"
            >
                {aisleItems}
            </ul>
        </nav>
    );
}

L1Navigation.propTypes = {
    aisles: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string.isRequired,
        })
    ),
};

L1Navigation.defaultProps = {
    aisles: [],
};
